import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, FileDown, ShieldCheck, Sheet } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { RagBadge } from "@/components/Rag";
import { Select } from "@/pages/Projects";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const Bar = ({ score, rag }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
    <div
      className="h-full rounded-full transition-all"
      style={{ width: `${score}%`, backgroundColor: rag === "Green" ? "#059669" : rag === "Amber" ? "#D97706" : "#DC2626" }}
    />
  </div>
);

export default function Compliance() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [portfolioId, setPortfolioId] = useState("");
  const [programmeId, setProgrammeId] = useState("");

  const load = useCallback(() => {
    const params = {};
    if (portfolioId) params.portfolio_id = portfolioId;
    if (programmeId) params.programme_id = programmeId;
    api.get("/compliance/scorecard", { params })
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(apiError(e.response?.data?.detail) || "Could not load the scorecard"));
  }, [portfolioId, programmeId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([api.get("/portfolios"), api.get("/programmes")])
      .then(([pf, pg]) => { setPortfolios(pf.data); setProgrammes(pg.data); })
      .catch(() => {});
  }, []);

  const download = async (fmt) => {
    try {
      const res = await api.get("/compliance/scorecard/export", {
        params: { fmt, portfolio_id: portfolioId || undefined, programme_id: programmeId || undefined },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `framework-compliance.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const s = data?.summary;

  return (
    <Shell
      testId="compliance-page"
      title="Framework Compliance"
      subtitle="Assurance scorecard — which projects are keeping to their governance framework."
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="compliance-export-xlsx-btn" onClick={() => download("xlsx")} className="gap-2">
            <Sheet size={15} /> Excel
          </Button>
          <Button data-testid="compliance-export-pdf-btn" onClick={() => download("pdf")} className="gap-2" style={{ backgroundColor: "var(--brand-primary)" }}>
            <FileDown size={15} /> PDF Pack
          </Button>
        </div>
      }
    >
      <div className="mb-6 flex flex-wrap items-end gap-4" data-testid="compliance-filters">
        <div className="w-56">
          <label className="field-label">Portfolio</label>
          <Select testId="compliance-portfolio-filter" value={portfolioId} onChange={setPortfolioId} options={portfolios.map((p) => ({ value: p.id, label: p.name }))} placeholder="All portfolios" />
        </div>
        <div className="w-56">
          <label className="field-label">Programme</label>
          <Select testId="compliance-programme-filter" value={programmeId} onChange={setProgrammeId} options={programmes.map((p) => ({ value: p.id, label: p.name }))} placeholder="All programmes" />
        </div>
        {(portfolioId || programmeId) && (
          <Button variant="outline" size="sm" data-testid="compliance-clear-filters-btn" onClick={() => { setPortfolioId(""); setProgrammeId(""); }}>Clear</Button>
        )}
      </div>

      {!data ? (
        <Empty testId="compliance-loading" text="Scoring projects…" />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="compliance-kpis">
            <div className="panel p-5" data-testid="compliance-kpi-average">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Average Compliance</div>
              <div className="mt-1 font-display text-3xl font-semibold text-slate-900">{s.average_score}%</div>
              <div className="mt-3"><Bar score={s.average_score} rag={s.average_score >= 90 ? "Green" : s.average_score >= 70 ? "Amber" : "Red"} /></div>
            </div>
            <div className="panel p-5" data-testid="compliance-kpi-assessed">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Projects Assessed</div>
              <div className="mt-1 font-display text-3xl font-semibold text-slate-900">{s.projects_assessed}</div>
              <div className="mt-2 text-xs text-slate-500">{s.projects_unassessed} without a framework template</div>
            </div>
            <div className="panel p-5" data-testid="compliance-kpi-rag">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Assurance Spread</div>
              <div className="mt-2 flex items-center gap-2">
                <RagBadge value="Red" testId="compliance-rag-red" /><span className="text-sm font-semibold">{s.rag.Red}</span>
                <RagBadge value="Amber" testId="compliance-rag-amber" /><span className="text-sm font-semibold">{s.rag.Amber}</span>
                <RagBadge value="Green" testId="compliance-rag-green" /><span className="text-sm font-semibold">{s.rag.Green}</span>
              </div>
            </div>
            <div className="panel p-5" data-testid="compliance-kpi-worst">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Weakest Check</div>
              {(() => {
                const worst = [...s.by_check].sort((a, b) => b.failed - a.failed)[0];
                return (
                  <>
                    <div className="mt-1 font-display text-lg font-semibold text-slate-900">{worst?.label || "—"}</div>
                    <div className="mt-1 text-xs text-slate-500">{worst?.failed || 0} projects failing</div>
                  </>
                );
              })()}
            </div>
          </div>

          <section className="panel p-5" data-testid="compliance-by-check">
            <h3 className="font-display text-base font-semibold text-slate-800">Compliance by check</h3>
            <div className="mt-4 space-y-3">
              {s.by_check.map((c) => {
                const total = c.passed + c.failed;
                const pct = total ? Math.round((c.passed / total) * 100) : 0;
                return (
                  <div key={c.key} data-testid={`check-bar-${c.key}`}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="text-slate-700">{c.label}</span>
                      <span className="text-slate-500">{c.passed}/{total} · {pct}%</span>
                    </div>
                    <Bar score={pct} rag={pct >= 90 ? "Green" : pct >= 70 ? "Amber" : "Red"} />
                  </div>
                );
              })}
            </div>
          </section>

          {data.projects.length === 0 ? (
            <Empty testId="compliance-empty" text="No projects have a configured governance framework yet." />
          ) : (
            <section className="panel overflow-x-auto" data-testid="compliance-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[10px] font-bold uppercase tracking-widest text-slate-400">
                    <th className="px-5 py-3">Project</th>
                    <th className="px-3 py-3">Framework</th>
                    <th className="px-3 py-3">Stage</th>
                    <th className="px-3 py-3">Score</th>
                    {data.checks.map((c) => <th key={c.key} className="px-3 py-3 text-center">{c.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.projects.map((r) => (
                    <tr key={r.project_id} data-testid={`compliance-row-${r.project_id}`} className="cursor-pointer border-b border-slate-100 last:border-0 transition-colors hover:bg-slate-50" onClick={() => navigate(`/projects/${r.project_id}`)}>
                      <td className="px-5 py-3">
                        <div className="font-medium text-slate-800">{r.project_name}</div>
                        <div className="text-xs text-slate-400">{r.portfolio_name || "Unassigned"} · {r.project_manager || "No PM"}</div>
                      </td>
                      <td className="px-3 py-3 text-slate-600">{r.framework}</td>
                      <td className="px-3 py-3 text-slate-600">{r.stage || "—"}</td>
                      <td className="w-32 px-3 py-3">
                        <div className="mb-1 flex items-center gap-2">
                          <span data-testid={`compliance-score-${r.project_id}`} className="text-sm font-semibold text-slate-800">{r.score}%</span>
                          <RagBadge value={r.compliance_rag} testId={`compliance-badge-${r.project_id}`} />
                        </div>
                        <Bar score={r.score} rag={r.compliance_rag} />
                      </td>
                      {data.checks.map((c) => (
                        <td key={c.key} className="px-3 py-3 text-center" title={r.checks[c.key].detail}>
                          {r.checks[c.key].pass ? (
                            <Check size={16} data-testid={`check-pass-${r.project_id}-${c.key}`} className="mx-auto text-emerald-600" />
                          ) : (
                            <X size={16} data-testid={`check-fail-${r.project_id}-${c.key}`} className="mx-auto text-red-500" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {data.unassessed.length > 0 && (
            <section className="panel p-5" data-testid="compliance-unassessed">
              <h3 className="flex items-center gap-2 font-display text-base font-semibold text-slate-800">
                <ShieldCheck size={16} className="text-slate-400" /> Unassessed projects
              </h3>
              <p className="mt-1 text-xs text-slate-500">Excluded from the score until a framework template is applied.</p>
              <div className="mt-3 divide-y divide-slate-100">
                {data.unassessed.map((u) => (
                  <div key={u.project_id} data-testid={`unassessed-row-${u.project_id}`} className="flex items-center justify-between gap-4 py-2.5">
                    <button onClick={() => navigate(`/projects/${u.project_id}`)} className="text-sm font-medium text-slate-800 hover:underline">{u.project_name}</button>
                    <span className="text-xs text-slate-500">{u.reason}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </Shell>
  );
}
